import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  EligibleClient,
  ReferralAgreement,
  ReferralPartner,
  ReferralService,
  ReferralTier,
} from '../../../services/referral.service';

/** Create or edit a referral (client ← partner, tier list). super_admin only. */
@Component({
  selector: 'app-referral-agreement-dialog',
  templateUrl: './referral-agreement-dialog.component.html',
  styleUrl: './referral-agreement-dialog.component.scss',
})
export class ReferralAgreementDialogComponent implements OnInit {
  /** Set when editing; client and partner are then fixed. */
  @Input() agreement?: ReferralAgreement;
  @Input() partners: ReferralPartner[] = [];
  /** Paying clients without a running referral (backend-filtered). */
  @Input() clients: EligibleClient[] = [];

  form!: FormGroup;
  saving = false;
  clientFilter = '';
  filteredClients: EligibleClient[] = [];

  /** lern.link proposal 09/2026 — only a pre-fill. */
  private readonly defaultTiers: ReferralTier[] = [
    { from_month: 1, to_month: 12, percent: 15 },
    { from_month: 13, to_month: null, percent: 5 },
  ];

  constructor(
    protected dialogRef: NbDialogRef<ReferralAgreementDialogComponent>,
    private fb: FormBuilder,
    private referralService: ReferralService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const a = this.agreement;
    const today = new Date().toISOString().slice(0, 10);
    this.form = this.fb.group({
      client_id: [{ value: a?.client_id ?? null, disabled: !!a }, Validators.required],
      partner_id: [
        { value: a?.partner_id ?? this.partners.find((p) => p.active)?.id ?? null, disabled: !!a },
        Validators.required,
      ],
      start_date: [a?.start_date ?? today, Validators.required],
      end_date: [a?.end_date ?? null],
      note: [a?.note ?? ''],
      tiers: this.fb.array((a?.tiers?.length ? a.tiers : this.defaultTiers).map((t) => this.tierGroup(t))),
    });
    this.applyClientFilter();
  }

  get tiers(): FormArray {
    return this.form.get('tiers') as FormArray;
  }

  private tierGroup(t: ReferralTier): FormGroup {
    return this.fb.group({
      from_month: [t.from_month, [Validators.required, Validators.min(1)]],
      to_month: [t.to_month],
      percent: [t.percent, [Validators.required, Validators.min(0), Validators.max(100)]],
    });
  }

  addTier() {
    const last = this.tiers.at(this.tiers.length - 1)?.value as ReferralTier | undefined;
    const from = last?.to_month ? Number(last.to_month) + 1 : (last?.from_month ?? 0) + 12;
    this.tiers.push(this.tierGroup({ from_month: from, to_month: null, percent: 0 }));
  }

  removeTier(i: number) {
    if (this.tiers.length > 1) this.tiers.removeAt(i);
  }

  applyClientFilter() {
    const q = this.clientFilter.trim().toLowerCase();
    this.filteredClients = q
      ? this.clients.filter((c) => `${c.name ?? ''} ${c.email ?? ''}`.toLowerCase().includes(q))
      : this.clients;
  }

  clientLabel(c: EligibleClient): string {
    const interval = this.translate.instant(
      c.billing_interval === 'year' ? 'dialogs.referralAgreement.yearly' : 'dialogs.referralAgreement.monthly'
    );
    const pastDue = c.subscription_status === 'past_due'
      ? ` · ${this.translate.instant('dialogs.referralAgreement.pastDue')}` : '';
    return `${c.name || c.email || c.id} · ${c.plan} ${interval}${pastDue}`;
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    const tiers: ReferralTier[] = v.tiers.map((t: any) => ({
      from_month: Number(t.from_month),
      to_month: t.to_month === null || t.to_month === '' ? null : Number(t.to_month),
      percent: Number(t.percent),
    }));
    this.saving = true;
    const req$ = this.agreement
      ? this.referralService.updateAgreement(this.agreement.id, {
          start_date: v.start_date,
          end_date: v.end_date || null,
          clear_end_date: !v.end_date,
          tiers,
          note: v.note || null,
        })
      : this.referralService.createAgreement({
          client_id: v.client_id,
          partner_id: v.partner_id,
          start_date: v.start_date,
          end_date: v.end_date || null,
          tiers,
          note: v.note || null,
        });
    req$.subscribe({
      next: (res) => {
        this.saving = false;
        if (res.stripe_warning) {
          this.toastr.warning(
            this.translate.instant('dialogs.referralAgreement.stripeWarning', { reason: res.stripe_warning }),
            ''
          );
        }
        this.dialogRef.close(res.data);
      },
      error: (err) => {
        this.saving = false;
        this.toastr.danger(err?.error?.detail || this.translate.instant('dialogs.referralAgreement.saveError'), '');
      },
    });
  }
}
